import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { ThemeProvider } from './context/ThemeContext';
import Header from './components/Header';
import LanguageSwitcher from './components/LanguageSwitcher';
import Footer from './components/Footer';
import Login from './components/Login';
import Signup from './components/Signup';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Profile from './components/Profile';
import EditProfile from './components/EditProfile';
import Terms from './components/Terms';
import PrivateRoute from './components/PrivateRoute';
import AdminRoute from './components/AdminRoute';
import UsersList from './components/UsersList';
import TeamsList from './components/TeamsList';
import EventsList from './components/EventsList';
import EventView from './components/EventView';
import EventResultsPage from './components/EventResultsPage';
import CreateEditTeam from './components/CreateEditTeam';
import CreateEditEvent from './components/CreateEditEvent';
import EventCreationGuide from './components/EventCreationGuide';
import Notifications from './components/Notifications';
import Home from './components/Home';
import OAuthCallback from './components/OAuthCallback';
import RMCDashboard from './components/RMCDashboard';
import RMCRules from './components/RMCRules';
import RMCAdminPanel from './components/RMCAdminPanel';
import RMCInviteAccept from './components/RMCInviteAccept';
import AdminAnalytics from './components/AdminAnalytics';
import SuggestionsPage from './components/SuggestionsPage';
import NewsPage from './components/NewsPage';
import RouvyReauthModal from './components/RouvyReauthModal';
import SupportWidget from './components/SupportWidget';
import RiderProfile from './components/RiderProfile';
import RatingsPage from './components/RatingsPage';
import WorkoutsPage from './components/WorkoutsPage';
import WorkoutView from './components/WorkoutView';
import logo from './logo.png';
import './App.css';

// Протокол события по прямой ссылке. Открыт всем, включая незалогиненных:
// ссылкой на результаты делятся, и требовать регистрацию, чтобы ее открыть,
// значит не делиться вовсе (см. Api::EventResultsController)
function PublicEventResults() {
  const { id } = useParams();

  return <EventResultsPage selectedEventId={id} />;
}

function AppContent() {
  const location = useLocation();
  const isProfilePage = location.pathname === '/profile';
  const isTermsPage = location.pathname === '/terms';
  const isHomePage = location.pathname === '/';

  return (
    <>
      <LanguageSwitcher />
      <div className="App" id="app-root">
        <Header />
        <div className={`app-content ${isProfilePage ? 'no-scroll' : ''} ${isTermsPage ? 'terms-page' : ''} ${isHomePage ? 'landing-page' : ''}`} style={{ '--logo-url': `url(${logo})` }}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/auth/callback" element={<OAuthCallback />} />
            <Route
              path="/profile"
              element={
                <PrivateRoute>
                  <Profile />
                </PrivateRoute>
              }
            />
            <Route
              path="/edit-profile"
              element={
                <PrivateRoute>
                  <EditProfile />
                </PrivateRoute>
              }
            />
            <Route path="/terms" element={<Terms />} />
            {/* Публичные страницы: карточка гонщика и протокол события */}
            <Route path="/riders/:id" element={<RiderProfile />} />
            <Route path="/events/:id/results" element={<PublicEventResults />} />
            <Route
              path="/users"
              element={
                <PrivateRoute>
                  <UsersList />
                </PrivateRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <PrivateRoute>
                  <Notifications />
                </PrivateRoute>
              }
            />
            <Route
              path="/events"
              element={
                <PrivateRoute>
                  <EventsList />
                </PrivateRoute>
              }
            />
            <Route
              path="/events/new"
              element={
                <PrivateRoute>
                  <CreateEditEvent />
                </PrivateRoute>
              }
            />
            <Route
              path="/events/guide"
              element={
                <PrivateRoute>
                  <EventCreationGuide />
                </PrivateRoute>
              }
            />
            <Route
              path="/events/:id"
              element={
                <PrivateRoute>
                  <EventView />
                </PrivateRoute>
              }
            />
            <Route
              path="/events/:id/edit"
              element={
                <PrivateRoute>
                  <CreateEditEvent />
                </PrivateRoute>
              }
            />
            <Route
              path="/results"
              element={
                <PrivateRoute>
                  <EventResultsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/ratings"
              element={
                <PrivateRoute>
                  <RatingsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/suggestions"
              element={
                <PrivateRoute>
                  <SuggestionsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/news"
              element={
                <PrivateRoute>
                  <NewsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/teams"
              element={
                <PrivateRoute>
                  <TeamsList />
                </PrivateRoute>
              }
            />
            <Route
              path="/teams/new"
              element={
                <PrivateRoute>
                  <CreateEditTeam />
                </PrivateRoute>
              }
            />
            <Route
              path="/teams/:id/edit"
              element={
                <PrivateRoute>
                  <CreateEditTeam />
                </PrivateRoute>
              }
            />
            <Route
              path="/workouts"
              element={
                <PrivateRoute>
                  <WorkoutsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/workouts/:id"
              element={
                <PrivateRoute>
                  <WorkoutView />
                </PrivateRoute>
              }
            />
            <Route
              path="/rmc"
              element={<RMCDashboard />}
            />
            <Route
              path="/rmc/rules"
              element={<RMCRules />}
            />
            <Route
              path="/rmc/invite/:token"
              element={<RMCInviteAccept />}
            />
            <Route
              path="/rmc/admin"
              element={
                <AdminRoute>
                  <RMCAdminPanel />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/analytics"
              element={
                <PrivateRoute>
                  <AdminAnalytics />
                </PrivateRoute>
              }
            />
            <Route path="/" element={<Home />} />
          </Routes>
        </div>
        <Footer />
      </div>
      <RouvyReauthModal />
      <SupportWidget />
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <Router>
            <AppContent />
          </Router>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;